/**
 * Every word the browser layer puts on screen, in English.
 *
 * This file defines the key set. `ne.ts` is typed against it, so adding a line
 * here fails the build until the Nepali exists — which is the whole mechanism. The
 * reason i18n was deferred was that half-translated is worse than untranslated, and
 * a catalog that cannot compile while half-filled is the answer to that.
 *
 * Keys are namespaced by the screen they belong to; `common.*` is anything that
 * appears on more than one. Placeholders are `{name}`, and a key ending `_one` is
 * used automatically when a `count` parameter is exactly 1.
 */
export const en = {
  'activity.actions_will_appear_here_as_your':
    'Actions will appear here as your team uses the platform.',
  'activity.as_alias': '(as {alias})',
  'activity.broken_at_entry_seq': 'Broken at entry {seq}',
  'activity.check_record': 'Check record',
  'activity.entries_verified': '{count} entries verified',
  'activity.entries_verified_one': '1 entry verified',
  'activity.every_change_and_who_made_it':
    'Every change, and who made it. This record can be added to but never edited.',
  'activity.failed': 'Failed',
  'activity.ledger': 'Ledger',
  'activity.nothing_has_happened_yet': 'Nothing has happened yet',
  'activity.show_older': 'Show older',
  'activity.someone': 'Someone',
  'auth.pitch.agree': 'Agree the price, then order',
  'auth.pitch.agree_body':
    'Ask for a better rate, settle it in writing, and the order locks itself in.',
  'auth.pitch.one_list': 'One price list',
  'auth.pitch.one_list_body':
    'Every shop, every rate, in one place — no more calling round for a number.',
  'auth.pitch.recorded': 'Nothing goes unrecorded',
  'auth.pitch.recorded_body':
    'Every change is stamped with who made it and when. It can be added to, never edited.',
  'auth_layout.accounts_are_issued_by_the_office': 'Accounts are issued by the office',
  'auth_layout.between_the_shop_and_the_site': 'Between the shop and the site.',
  'auth_layout.wholesale_hardware_brokered': 'Wholesale hardware, brokered',
  'brand.hardware': 'Hardware',
  'brand.mediator': 'Mediator',
  'businesses.9_digits_if_registered': '9 digits, if registered.',
  'businesses.a_supplier_shop_you_buy_from':
    'A supplier shop you buy from, or a customer you sell to.',
  'businesses.add_a_business': 'Add a business',
  'businesses.add_business': 'Add business',
  'businesses.add_the_supplier_shops_you_buy':
    'Add the supplier shops you buy from and the customers you sell to.',
  'businesses.address': 'Address',
  'businesses.all_businesses': 'All businesses',
  'businesses.city': 'City',
  'businesses.customers': 'Customers',
  'businesses.directory': 'Directory',
  'businesses.filter_by_type': 'Filter by type',
  'businesses.its_people_are_signed_out_immediately':
    'Its people are signed out immediately and its prices stop counting towards what we can supply. Orders already placed are unaffected.',
  'businesses.its_people_will_be_able_to':
    'Its people will be able to sign in again, and it can trade as before.',
  'businesses.name_is_active_again': '{name} is active again.',
  'businesses.no_businesses_yet': 'No businesses yet',
  'businesses.no_contact_details': 'No contact details',
  'businesses.notes': 'Notes',
  'businesses.only_your_team_can_see_these': 'Only your team can see these.',
  'businesses.pan_vat': 'PAN / VAT',
  'businesses.phone': 'Phone',
  'businesses.search_by_name_phone_or_city': 'Search by name, phone or city',
  'businesses.supplier_shop': 'Supplier shop',
  'businesses.supplier_shops': 'Supplier shops',
  'businesses.supplier_shops_and_customers_both_are':
    'Supplier shops and customers. Both are created here — nobody signs up on their own.',
  'businesses.try_a_different_search_or_filter': 'Try a different search or filter.',
  'businesses.type': 'Type',
  'businesses.you_buy_from_them': 'You buy from them',
  'businesses.you_sell_to_them': 'You sell to them',
  'catalog.categories.add_a_category': 'Add a category',
  'catalog.categories.add_a_sub_category': 'Add a sub-category',
  'catalog.categories.add_category': 'Add category',
  'catalog.categories.add_one_myself': 'Add one myself',
  'catalog.categories.back_to_items': 'Back to items',
  'catalog.categories.category_added': 'Category added',
  'catalog.categories.how_customers_narrow_the_catalog_down':
    'How customers narrow the catalog down. Two levels is the limit — deeper trees stop being useful.',
  'catalog.categories.inside': 'Inside',
  'catalog.categories.n_added': '{count} categories added',
  'catalog.categories.n_added_one': '1 category added',
  'catalog.categories.no_categories_yet': 'No categories yet',
  'catalog.categories.no_sub_categories': 'No sub-categories.',
  'catalog.categories.start_from_a_standard_hardware_set':
    'Start from a standard hardware set and edit it, or build your own from scratch.',
  'catalog.categories.sub_category': 'Sub-category',
  'catalog.categories.top_level': 'Top level',
  'catalog.categories.use_the_standard_set': 'Use the standard set',
  'catalog.price_books.add_a_customer': 'Add a customer',
  'catalog.price_books.add_them': 'Add them',
  'catalog.price_books.archive': 'Archive',
  'catalog.price_books.archived': 'Archived',
  'catalog.price_books.archived_those_customers_pay_the_published':
    'Archived — those customers pay the published price again',
  'catalog.price_books.arrangement_created': 'Arrangement created',
  'catalog.price_books.arrangement_updated': 'Arrangement updated',
  'catalog.price_books.buying_on_their_own': 'Buying on their own',
  'catalog.price_books.contractor_rate': 'Contractor rate',
  'catalog.price_books.customer_arrangements': 'Customer arrangements',
  'catalog.price_books.discount_off_every_price': 'Discount off every price',
  'catalog.price_books.edit_arrangement': 'Edit arrangement',
  'catalog.price_books.everyone_pays_the_published_price_add':
    'Everyone pays the published price. Add an arrangement for a customer who has negotiated a standing rate.',
  'catalog.price_books.flat_rate_set': 'Flat rate set',
  'catalog.price_books.flat_rates': 'Flat rates',
  'catalog.price_books.new_arrangement': 'New arrangement',
  'catalog.price_books.no_arrangements_yet': 'No arrangements yet',
  'catalog.price_books.none_everything_is_the_published_price':
    'None — everything is the published price less',
  'catalog.price_books.off': 'off',
  'catalog.price_books.on_this_arrangement': 'On this arrangement',
  'catalog.price_books.one_price_for_this_item_at':
    'One price for this item at every quantity, whatever the published ladder says. It can never be more than the published price.',
  'catalog.price_books.remove_name': 'Remove {name}',
  'catalog.price_books.remove_the_flat_rate_for_name': 'Remove the flat rate for {name}',
  'catalog.price_books.set_a_flat_rate': 'Set a flat rate',
  'catalog.price_books.set_it': 'Set it',
  'catalog.price_books.standing_rates_for_the_customers_who':
    'Standing rates for the customers who have earned one. A customer can be on at most one.',
  'catalog.price_books.the_discount_comes_off_every_published':
    'The discount comes off every published price, up to {maxpricebookdiscountbps0}. Anything deeper belongs in a quote.',
  'catalog.price_books.their_price_each': 'Their price, each',
  'catalog.price_books.they_start_paying_these_rates_on':
    'They start paying these rates on their next page load.',
  'catalog.price_books.who_agreed_it_and_when': 'Who agreed it, and when.',
  'catalog.pricing.applies_to_the_rows_currently_on':
    'Applies to the rows currently on screen — narrow with the search box first.',
  'catalog.pricing.changed_changed_not_text_text2': '{changed} changed. Not {text} — {text2}',
  'catalog.pricing.do_this_to_everything_shown': 'Do this to everything shown',
  'catalog.pricing.draft': 'Draft',
  'catalog.pricing.edit_prices_for_name': 'Edit prices for {name}',
  'catalog.pricing.from': 'from',
  'catalog.pricing.hidden': 'Hidden',
  'catalog.pricing.hidden_from_customers': 'Hidden from customers',
  'catalog.pricing.hide': 'Hide',
  'catalog.pricing.hide_all_n': 'Hide all {count}',
  'catalog.pricing.hide_from_customers': 'Hide from customers',
  'catalog.pricing.hide_them': 'Hide them',
  'catalog.pricing.hide_these_from_customers': 'Hide these from customers',
  'catalog.pricing.item_priced_publish_it_when_ready': 'Item priced — publish it when ready',
  'catalog.pricing.length_item_s_come_off_the':
    '{length} item(s) come off the shop. Existing orders are untouched.',
  'catalog.pricing.length_item_s_go_live_at':
    '{length} item(s) go live at their current prices. Anything without a price is left alone and named.',
  'catalog.pricing.live': 'Live',
  'catalog.pricing.live_for_customers': 'Live for customers',
  'catalog.pricing.n_items_changed': '{count} items changed',
  'catalog.pricing.n_items_changed_one': '1 item changed',
  'catalog.pricing.n_price_breaks': '{count} price breaks',
  'catalog.pricing.n_price_breaks_one': '1 price break',
  'catalog.pricing.no_price_set': 'No price set',
  'catalog.pricing.nothing_priced_yet': 'Nothing priced yet',
  'catalog.pricing.pick_an_item_from_the_catalog':
    'Pick an item from the catalog and set what customers pay for it.',
  'catalog.pricing.price_an_item': 'Price an item',
  'catalog.pricing.price_the_first_item': 'Price the first item',
  'catalog.pricing.prices_updated': 'Prices updated',
  'catalog.pricing.publish': 'Publish',
  'catalog.pricing.publish_all_n': 'Publish all {count}',
  'catalog.pricing.publish_them': 'Publish them',
  'catalog.pricing.publish_these_to_customers': 'Publish these to customers',
  'catalog.pricing.publish_to_customers': 'Publish to customers',
  'catalog.pricing.save_prices': 'Save prices',
  'catalog.pricing.search_priced_items': 'Search priced items',
  'catalog.pricing.set_what_a_customer_pays_and':
    'Set what a customer pays, and how the price drops as they order more.',
  'catalog.pricing.the_price_list_customers_see_and':
    'The price list customers see, and what it earns against the best shop price.',
  'catalog.products.add_item': 'Add item',
  'catalog.products.add_the_first_item': 'Add the first item',
  'catalog.products.add_the_items_you_trade_shops':
    'Add the items you trade. Shops can only list against something that is already here.',
  'catalog.products.description': 'Description',
  'catalog.products.dimension_area': 'Area',
  'catalog.products.dimension_count': 'Count and packaging',
  'catalog.products.dimension_length': 'Length',
  'catalog.products.dimension_mass': 'Weight',
  'catalog.products.dimension_volume': 'Volume',
  'catalog.products.filter_by_category': 'Filter by category',
  'catalog.products.how_it_should_appear_everywhere': 'How it should appear everywhere.',
  'catalog.products.item_name': 'Item name',
  'catalog.products.keep_this_item': 'Keep this item',
  'catalog.products.measured_in': 'Measured in',
  'catalog.products.merge': 'Merge',
  'catalog.products.merge_duplicate_item': 'Merge duplicate item',
  'catalog.products.merge_name_into_another_item': 'Merge {name} into another item',
  'catalog.products.merged_into_name': 'Merged into {name}',
  'catalog.products.model_or_grade': 'Model or grade',
  'catalog.products.name_will_point_at_the_item':
    '"{name}" will point at the item you pick. Its name is kept as a search term, so anyone looking for it still lands in the right place.',
  'catalog.products.one_shared_list_of_everything_the':
    'One shared list of everything the platform trades. Shops price against it; customers browse it.',
  'catalog.products.one_unit_contains': 'One unit contains',
  'catalog.products.optional_e_g_50_for_a': 'Optional. e.g. 50 for a 50 kg bag.',
  'catalog.products.removed': 'Removed',
  'catalog.products.search_by_name_or_brand': 'Search by name or brand',
  'catalog.products.search_for_the_item_to_keep': 'Search for the item to keep…',
  'catalog.products.the_catalog_is_empty': 'The catalog is empty',
  'catalog.products.the_unit_customers_order_in_and':
    'The unit customers order in and prices are quoted in.',
  'catalog.products.this_becomes_the_canonical_name_every':
    'This becomes the canonical name every shop and customer sees.',
  'catalog.products.try_a_different_search_or_a':
    'Try a different search, or a different category.',
  'catalog.suggestions.add_as_new': 'Add as new',
  'catalog.suggestions.add_to_catalog': 'Add to catalog',
  'catalog.suggestions.all_suggestions': 'All suggestions',
  'catalog.suggestions.change_the_filter_to_see_other':
    'Change the filter to see other suggestions.',
  'catalog.suggestions.done_the_shop_can_list_it': 'Done — the shop can list it now',
  'catalog.suggestions.match_it': 'Match it',
  'catalog.suggestions.match_to': 'Match to',
  'catalog.suggestions.matched': 'Matched',
  'catalog.suggestions.name_kept_as_search_term':
    '"{name}" is kept as a search term, so the next shop to type it finds the right item.',
  'catalog.suggestions.no_suggestions': 'No suggestions',
  'catalog.suggestions.not_added': 'Not added',
  'catalog.suggestions.note_for_the_shop': 'Note for the shop',
  'catalog.suggestions.optional': 'Optional.',
  'catalog.suggestions.put_it_in': 'Put it in',
  'catalog.suggestions.review': 'Review',
  'catalog.suggestions.review_suggestion': 'Review suggestion',
  'catalog.suggestions.search_the_item_list': 'Search the item list…',
  'catalog.suggestions.shops_suggest_what_they_cannot_find':
    'Shops suggest what they cannot find. Approve it as a new item, or match it to one we already have.',
  'catalog.suggestions.suggested_by_ashop_sold_by_the':
    'Suggested by {ashop}, sold by the {tolowercase}.',
  'catalog.suggestions.suggestion_turned_down': 'Suggestion turned down',
  'catalog.suggestions.they_will_see_this_so_make': 'They will see this, so make it useful.',
  'catalog.suggestions.turn_down': 'Turn down',
  'catalog.suggestions.waiting_for_review': 'Waiting for review',
  'catalog.suggestions.we_have_it': 'We have it',
  'catalog.suggestions.when_a_shop_cannot_find_an':
    'When a shop cannot find an item, their suggestion lands here.',
  'catalog.suggestions.why_not': 'Why not',
  'change_password.at_least_minpasswordlength_characters_pick_something':
    'At least {minpasswordlength} characters. Pick something you will remember.',
  'change_password.choose_your_password': 'Choose your password',
  'change_password.current_password': 'Current password',
  'change_password.new_password': 'New password',
  'change_password.password_updated': 'Password updated',
  'change_password.the_one_the_office_gave_you': 'The one the office gave you.',
  'change_password.type_it_again': 'Type it again',
  'change_password.your_account_was_set_up_with':
    'Your account was set up with a temporary password. Pick your own to continue.',
  'chat.inbox.ask_us_anything_we_answer_the': 'Ask us anything. We answer the same day.',
  'chat.inbox.cement_for_the_bhaktapur_site': 'Cement for the Bhaktapur site',
  'chat.inbox.every_conversation_with_a_customer_or':
    'Every conversation with a customer or a shop. Take one so the others know you are on it — anybody can still step in.',
  'chat.inbox.message': 'Message',
  'chat.inbox.messages_from_customers_and_shops_land':
    'Messages from customers and shops land here.',
  'chat.inbox.new_message': 'New message',
  'chat.inbox.no_conversations': 'No conversations',
  'chat.inbox.no_messages_yet': 'No messages yet',
  'chat.inbox.nothing_waiting_on_you': 'Nothing waiting on you',
  'chat.inbox.pick_a_business': 'Pick a business',
  'chat.inbox.pick_who_this_is_with': 'Pick who this is with.',
  'chat.inbox.search_conversations': 'Search conversations',
  'chat.inbox.talk_to_admingroupalias_about_anything_prices':
    'Talk to {admingroupalias} about anything — prices, stock, or a better rate.',
  'chat.inbox.waiting_on_us': 'Waiting on us',
  'chat.inbox.we_will_get_back_to_you': 'We will get back to you the same day.',
  'chat.inbox.what_is_it_about': 'What is it about',
  'chat.inbox.with': 'With',
  'chat.thread.all_messages': 'All messages',
  'chat.thread.attachment': 'Attachment',
  'chat.thread.closed': 'Closed',
  'chat.thread.conversation': 'Conversation',
  'chat.thread.conversation_closed': 'Conversation closed',
  'chat.thread.enter_sends_shift_enter_for_a': 'Enter sends · Shift + Enter for a new line',
  'chat.thread.internal': ' · internal',
  'chat.thread.internal_note': 'Internal note',
  'chat.thread.is_handling_it': 'is handling it',
  'chat.thread.note_for_the_desk_only': 'Note for the desk only…',
  'chat.thread.nothing_said_yet_start_the_conversation':
    'Nothing said yet. Start the conversation below.',
  'chat.thread.open_the_order': 'Open the order',
  'chat.thread.open_the_price_request': 'Open the price request',
  'chat.thread.price_suggested': 'Price suggested',
  'chat.thread.this_conversation_is_closed_start_a':
    'This conversation is closed. Start a new one if you need us.',
  'chat.thread.write_a_message': 'Write a message…',
  'common.accept': 'Accept',
  'common.activity': 'Activity',
  'common.add': 'Add',
  'common.add_an_item': 'Add an item',
  'common.add_the_first_one': 'Add the first one',
  'common.added': 'Added',
  'common.all': 'All',
  'common.all_categories': 'All categories',
  'common.appearance': 'Appearance',
  'common.back_in_the_queue': 'Back in the queue',
  'common.brand': 'Brand',
  'common.browse_the_shop': 'Browse the shop',
  'common.business_name': 'Business name',
  'common.businesses': 'Businesses',
  'common.buy_more_pay_less': 'Buy more, pay less',
  'common.call': 'Call',
  'common.cancel': 'Cancel',
  'common.catalog': 'Catalog',
  'common.categories': 'Categories',
  'common.category': 'Category',
  'common.change': 'Change',
  'common.check_prices_with_shops': 'Check prices with shops',
  'common.close': 'Close',
  'common.customer': 'Customer',
  'common.customer_prices': 'Customer prices',
  'common.deactivate': 'Deactivate',
  'common.deactivate_name': 'Deactivate {name}?',
  'common.deliver_to': 'Deliver to',
  'common.delivery': 'Delivery',
  'common.delivery_city': 'Delivery city',
  'common.desk': 'Desk',
  'common.discount': 'Discount',
  'common.edit': 'Edit',
  'common.email': 'Email',
  'common.everything': 'Everything',
  'common.filter': 'Filter',
  'common.filter_by_status': 'Filter by status',
  'common.finished': 'Finished',
  'common.goods': 'Goods',
  'common.how_much': 'How much',
  'common.item': 'Item',
  'common.item_list': 'Item list',
  'common.kathmandu': 'Kathmandu',
  'common.messages': 'Messages',
  'common.mine': 'Mine',
  'common.mobile_number': 'Mobile number',
  'common.money': 'Money',
  'common.my_items': 'My items',
  'common.n_items': '{count} items',
  'common.n_items_one': '1 item',
  'common.name': 'Name',
  'common.name_is_deactivated': '{name} is deactivated.',
  'common.no_matches': 'No matches',
  'common.no_orders_yet': 'No orders yet',
  'common.no_price_checks': 'No price checks',
  'common.no_price_set': 'No price set',
  'common.nobody_has_taken': 'Nobody has taken',
  'common.not_sorted_yet': 'Not sorted yet',
  'common.note': 'Note',
  'common.nothing_waiting': 'Nothing waiting',
  'common.notifications': 'Notifications',
  'common.numbers': 'Numbers',
  'common.office': 'Office',
  'common.open': 'Open',
  'common.orders': 'Orders',
  'common.orders_to_fill': 'Orders to fill',
  'common.our_stock': 'Our stock',
  'common.password': 'Password',
  'common.people': 'People',
  'common.period': 'Period',
  'common.pick_one': 'Pick one…',
  'common.price_checks': 'Price checks',
  'common.price_each': 'Price each',
  'common.price_request': 'Price request',
  'common.price_requests': 'Price requests',
  'common.put_it_back': 'Put it back',
  'common.quote_sent': 'Quote sent',
  'common.reactivate': 'Reactivate',
  'common.reactivate_name': 'Reactivate {name}?',
  'common.ready_in_days': 'Ready in (days)',
  'common.record_it': 'Record it',
  'common.running_low': 'Running low',
  'common.save': 'Save',
  'common.save_new_password': 'Save new password',
  'common.saved': 'Saved',
  'common.search_by_reference': 'Search by reference',
  'common.search_the_item_list': 'Search the item list…',
  'common.send': 'Send',
  'common.settings': 'Settings',
  'common.shop': 'Shop',
  'common.shop_orders': 'Shop orders',
  'common.shop_prices': 'Shop prices',
  'common.show_more': 'Show more',
  'common.sign_out': 'Sign out',
  'common.smallest_order': 'Smallest order',
  'common.smallest_order_is': 'Smallest order is',
  'common.sold_by_the': 'Sold by the',
  'common.sold_by_the_tolowercase': 'sold by the {tolowercase}',
  'common.something_went_wrong': 'Something went wrong. Please try again.',
  'common.start_one': 'Start one',
  'common.still_going': 'Still going',
  'common.suggested_items': 'Suggested items',
  'common.suspended': 'Suspended',
  'common.take_it': 'Take it',
  'common.take_it_over': 'Take it over',
  'common.temporary_password': 'Temporary password',
  'common.total': 'Total',
  'common.turn_it_down': 'Turn it down',
  'common.turn_off': 'Turn off',
  'common.turn_on': 'Turn on',
  'common.vat': 'VAT',
  'common.waiting': 'Waiting',
  'common.where_it_comes_from': 'Where it comes from',
  'common.you_have_this_one': 'You have this one',
  'common.your_basket': 'Your basket',
  'common.your_shop': 'Your shop',
  'contact.no_longer_here': 'No longer here',
  'contact.no_number_on_file_add_one': 'No number on file. Add one on the business or the person.',
  'contact.tap_a_number_to_ring_it': 'Tap a number to ring it.',
  'contact.the_shop_counter': 'The counter',
  'deals.allocations.all_from_our_stock': 'All from our stock',
  'deals.allocations.from_a_shop_order': 'From a shop order',
  'deals.allocations.fully_sourced': 'Fully sourced',
  'deals.allocations.nothing_raised_for_this_order_yet':
    'Nothing raised for this order yet. Order at a shop’s listed rate and it appears here — or ask a few shops first, if nobody lists it.',
  'deals.allocations.only_shop_orders_already_raised_for':
    'Only shop orders already raised for this order can cover it.',
  'deals.allocations.order_from_a_shop': 'Order from a shop',
  'deals.allocations.remove_this_source': 'Remove this source',
  'deals.allocations.source_recorded': 'Source recorded',
  'deals.allocations.source_this_from_a_shop': 'Source this from a shop',
  'deals.allocations.unsourced': 'unsourced',
  'deals.allocations.up_to_v0_still_unsourced': 'Up to {v0} still unsourced.',
  'deals.allocations.which_shop_order': 'Which shop order',
  'deals.order_detail.all_orders': 'All orders',
  'deals.order_detail.anything_reserved_for_this_order_goes':
    'Anything reserved for this order goes back into stock.',
  'deals.order_detail.confirm': 'Confirm',
  'deals.order_detail.from_the_price_request': 'From the price request',
  'deals.order_detail.mark_as': 'Mark as',
  'deals.order_detail.mark_as_tolowercase': 'Mark as {tolowercase}',
  'deals.order_detail.moved_to_thenextstep': 'Moved to {thenextstep}',
  'deals.order_detail.optional_note_the_customer_sees_this':
    'Optional note — the customer sees this.',
  'deals.order_detail.order': 'Order',
  'deals.order_detail.order_this_again': 'Order this again',
  'deals.order_detail.placed_placedat': 'Placed {placedat}',
  'deals.order_detail.print': 'Print',
  'deals.order_detail.progress': 'Progress',
  'deals.order_detail.the_customer_is_notified': 'The customer is notified.',
  'deals.order_detail.this_also_takes_the_goods_off': 'This also takes the goods off our shelf.',
  'deals.orders.accept_a_quote_and_the_order': 'Accept a quote and the order shows up here.',
  'deals.orders.an_order_appears_the_moment_a': 'An order appears the moment a quote is accepted.',
  'deals.orders.everything_agreed_and_where_it_has': 'Everything agreed, and where it has got to.',
  'deals.orders.locked_deals_move_each_one_along':
    'Locked deals. Move each one along as it is packed, sent and delivered.',
  'deals.orders.nothing_in_progress': 'Nothing in progress',
  'deals.orders.your_orders': 'Your orders',
  'deals.purchase_orders.all_shop_orders': 'All shop orders',
  'deals.purchase_orders.book_these_goods_in': 'Book these goods in',
  'deals.purchase_orders.buy_side': 'Buy side',
  'deals.purchase_orders.cancel_code': 'Cancel {code}?',
  'deals.purchase_orders.cancel_the_order': 'Cancel the order',
  'deals.purchase_orders.cancel_this_order': 'Cancel this order',
  'deals.purchase_orders.covers_code': 'Covers {code}',
  'deals.purchase_orders.each': 'each',
  'deals.purchase_orders.expected_when': 'expected {when}',
  'deals.purchase_orders.for_code': 'For {code}',
  'deals.purchase_orders.goods_booked_in': 'Goods booked in',
  'deals.purchase_orders.goods_in': 'Goods in',
  'deals.purchase_orders.no_shop_orders_yet': 'No shop orders yet',
  'deals.purchase_orders.nothing_on_order': 'Nothing on order',
  'deals.purchase_orders.order_at_a_shops_listed_rate':
    'Order at a shop’s listed rate, or ask a few of them for one first.',
  'deals.purchase_orders.order_cancelled': 'Order cancelled',
  'deals.purchase_orders.ordered_createdattext': 'Ordered {createdat}{text}',
  'deals.purchase_orders.orgname_is_told_at_once_and':
    '{orgname} is told at once, and anything this was covering goes back to needing a source.',
  'deals.purchase_orders.stock_buy': 'Stock buy',
  'deals.purchase_orders.the_full_quantity_goes_onto_our':
    'The full quantity goes onto our own shelf, and {orgname} becomes owed the full amount.',
  'deals.purchase_orders.these_are_for_ordercode_so_they':
    'These are for {ordercode}, so they pass straight through and our own shelf is untouched. {orgname} becomes owed the full amount.',
  'deals.purchase_orders.what_we_asked_for': 'What we asked for',
  'deals.purchase_orders.what_we_have_ordered_in_and':
    'What we have ordered in, and whether the shop has said yes.',
  'deals.request_detail.accept_and_order': 'Accept and order',
  'deals.request_detail.accept_their_price': 'Accept their price',
  'deals.request_detail.agreed_the_order_is_confirmed': 'Agreed — the order is confirmed',
  'deals.request_detail.all_price_requests': 'All price requests',
  'deals.request_detail.another_supplier_quoted_less_or_the':
    'Another supplier quoted less, or the budget is fixed…',
  'deals.request_detail.any_blank_line_is_priced_from':
    'Any blank line is priced from the live ladder, so the final figure may differ.',
  'deals.request_detail.asked_createdat': 'Asked {createdat}',
  'deals.request_detail.became_an_order_open_it': 'Became an order — open it',
  'deals.request_detail.deliver_to_deliverycity': 'Deliver to {deliverycity}',
  'deals.request_detail.how_the_price_moved': 'How the price moved',
  'deals.request_detail.is_handling_it': 'is handling it',
  'deals.request_detail.keep_it_open': 'Keep it open',
  'deals.request_detail.leave_a_price_blank_to_use':
    'Leave a price blank to use the listed rate. Everything is recomputed server-side.',
  'deals.request_detail.listed_price': 'Listed price',
  'deals.request_detail.new_quote': 'New quote',
  'deals.request_detail.no_quote_yet': 'No quote yet',
  'deals.request_detail.note_to_the_customer': 'Note to the customer',
  'deals.request_detail.nothing_quoted_yet_check_the_shops':
    'Nothing quoted yet. Check the shops if you need a cost first, then send a price.',
  'deals.request_detail.optional_what_would_work_instead': 'Optional — what would work instead?',
  'deals.request_detail.put_your_numbers_in_we_will':
    'Put your numbers in. We will look at them and reply.',
  'deals.request_detail.quoted': '· quoted',
  'deals.request_detail.ran_out_of_time': 'Ran out of time',
  'deals.request_detail.send_a_quote': 'Send a quote',
  'deals.request_detail.send_my_price': 'Send my price',
  'deals.request_detail.send_quote': 'Send quote',
  'deals.request_detail.sent_we_will_come_back_to': 'Sent — we will come back to you',
  'deals.request_detail.suggest_a_different_price': 'Suggest a different price',
  'deals.request_detail.the_conversation_stays_open_so_a':
    'The conversation stays open, so a new price can still be agreed.',
  'deals.request_detail.they_pay': 'They pay',
  'deals.request_detail.this_quote_has_run_out_of': 'This quote has run out of time.',
  'deals.request_detail.turn_this_quote_down': 'Turn this quote down',
  'deals.request_detail.turned_down': 'Turned down',
  'deals.request_detail.valid_for_hours': 'Valid for (hours)',
  'deals.request_detail.valid_until_expiresat': 'Valid until {expiresat}.',
  'deals.request_detail.we_have_your_request_a_price':
    'We have your request. A price will appear here shortly and you will be notified.',
  'deals.request_detail.what_was_asked_for': 'What was asked for',
  'deals.request_detail.why': 'Why',
  'deals.request_detail.you': 'You',
  'deals.request_detail.your_price_request': 'Your price request',
  'deals.requests.customers_asking_for_a_rate_claim':
    'Customers asking for a rate. Claim one, send a quote, and keep the clock short.',
  'deals.requests.everything_you_have_asked_us_to':
    'Everything you have asked us to price, and where each one got to.',
  'deals.requests.fill_your_basket_and_ask_us':
    'Fill your basket and ask us for a rate — we usually come back the same day.',
  'deals.requests.no_requests': 'No requests',
  'deals.requests.still_open': 'Still open',
  'deals.requests.unclaimed': 'Unclaimed',
  'deals.requests.when_a_customer_asks_for_a':
    'When a customer asks for a price, it lands here with a timer running.',
  'deals.requests.your_requests': 'Your requests',
  'deals.shop_prices.ask_a_few_of_them_what': 'Ask a few of them what they would charge.',
  'deals.shop_prices.ask_shops_instead': 'Ask shops instead',
  'deals.shop_prices.call_them_first': 'Ringing first?',
  'deals.shop_prices.look_up_an_item': 'Look up an item',
  'deals.shop_prices.no_rate_on_file': 'No rate on file',
  'deals.shop_prices.no_shop_lists_this_item': 'No shop lists this item',
  'deals.shop_prices.order_from_shop': 'Order from {shop}',
  'deals.shop_prices.other_shops': 'Other shops',
  'deals.shop_prices.pick_an_item_and_a_quantity':
    'Pick an item and a quantity to see what the shops charge.',
  'deals.shop_prices.pre_filled_with_their_listed_rate':
    'Pre-filled with their listed rate. Change it if they agreed something else.',
  'deals.shop_prices.rates_for_qty_of_item': 'Rates for {qty} of {item}.',
  'deals.shop_prices.ready_in_days': 'ready in {days} days',
  'deals.shop_prices.they_are_told_at_once_and':
    'They are told at once, and it goes onto this order’s worksheet.',
  'deals.shop_prices.under_their_minimum_of_moq': 'Under their minimum of {moq}',
  'deals.shop_prices.what_every_shop_charges_right_now':
    'What every shop charges right now. Nothing to wait for.',
  'deals.sourcing.answer_within_hours': 'Answer within (hours)',
  'deals.sourcing.ask_them': 'Ask them',
  'deals.sourcing.ask_these_shops': 'Ask these shops',
  'deals.sourcing.asked_length_shops': 'Asked {length} shop{s}',
  'deals.sourcing.best': 'best',
  'deals.sourcing.look_up_what_shops_charge_and':
    'Look up what shops already charge, or ask them when nobody lists it.',
  'deals.sourcing.new_price_check': 'New price check',
  'deals.sourcing.no_supplier_shops_yet_add_one':
    'No supplier shops yet. Add one under Businesses first.',
  'deals.sourcing.note_to_the_shops': 'Note to the shops',
  'deals.sourcing.of': 'of',
  'deals.sourcing.pick_an_item_and_a_handful':
    'Pick an item and a handful of shops, and the quotes come back here.',
  'deals.sourcing.priced': 'priced',
  'deals.sourcing.supply': 'Supply',
  'deals.sourcing.they_see_the_item_the_quantity':
    'They see the item, the quantity and the city. Nothing about the customer.',
  'deals.sourcing_detail.all_price_checks': 'All price checks',
  'deals.sourcing_detail.cheapest': 'Cheapest',
  'deals.sourcing_detail.code_sent_to_org': '{code} sent to {org}',
  'deals.sourcing_detail.deadline_passed': 'Deadline passed',
  'deals.sourcing_detail.due_dueat': 'Due {dueat}',
  'deals.sourcing_detail.each': 'each ·',
  'deals.sourcing_detail.finish': 'Finish',
  'deals.sourcing_detail.for_a_customer_request': 'For a customer request',
  'deals.sourcing_detail.nothing_back_yet_shops_are_notified':
    'Nothing back yet. Shops are notified as soon as you ask them.',
  'deals.sourcing_detail.order_from_text': 'Order from {text}',
  'deals.sourcing_detail.order_from_them': 'Order from them',
  'deals.sourcing_detail.part_only': 'Part only',
  'deals.sourcing_detail.pre_filled_with_what_they_quoted': 'Pre-filled with what they quoted.',
  'deals.sourcing_detail.price_check_finished': 'Price check finished',
  'deals.sourcing_detail.covers_code': 'Covers {code}',
  'deals.sourcing_detail.qty_available': '{qty} available',
  'deals.sourcing_detail.quotes_in': 'Quotes in',
  'deals.sourcing_detail.ready_in_leadtimedays_days': ' · ready in {leadtimedays} days',
  'deals.sourcing_detail.recorded_against_code':
    'This will be recorded against {code}, so its supply worksheet fills in.',
  'deals.sourcing_detail.send_the_order': 'Send the order',
  'deals.sourcing_detail.still_waiting': 'Still waiting',
  'deals.sourcing_detail.they_are_told_straight_away_and':
    'They are told straight away and can accept or pass. Nothing about the customer goes with it.',
  'deals.sourcing_detail.to_city': ' to {city}',
  'deals.sourcing_detail.total': 'total',
  'deals.sourcing_detail.unitcodedeliverycity_asked_createdat':
    '{unitcode}{deliverycity} · asked {createdat}',
  'home.buyers_and_sellers_only_ever_talk':
    'Buyers and sellers only ever talk to you. Both sides stay anonymous to each other.',
  'home.connected': 'Connected',
  'home.everything_you_need_at_a_price':
    'Everything you need, at a price that drops as the order grows.',
  'home.good_afternoon': 'Good afternoon',
  'home.good_evening': 'Good evening',
  'home.good_morning': 'Good morning',
  'home.keep_your_prices_current_and_answer':
    'Keep your rates current — the desk buys straight off them.',
  'home.keep_your_rates_current_and_answer':
    'Keep your rates current — the desk buys straight off them. {admingroupalias} handles the customers.',
  'home.one_desk_for_every_price_quote': 'One desk for every price, quote and order.',
  'home.orders_without_chasing_customers': 'Orders without chasing customers.',
  'home.quiet.activity': 'Activity log',
  'home.talk_to_admingroupalias_for_anything_prices':
    'Talk to {admingroupalias} for anything — prices, stock, or a better rate on a big order.',
  'home.tile.cart_desc': 'Set quantities and ask for a price.',
  'home.tile.items_desc': 'What you sell and what you charge.',
  'home.tile.messages_desc': 'Conversations with shops and customers.',
  'home.tile.money_desc': 'Who owes us, and who we owe.',
  'home.tile.orders_desc': 'Locked deals and where they got to.',
  'home.tile.price_checks_desc': 'Rates we have asked you for.',
  'home.tile.pricing_desc': 'What customers pay, and the margin.',
  'home.tile.requests_desc': 'Customers waiting for a rate.',
  'home.tile.shop_desc': 'Browse everything we supply.',
  'home.tile.sourcing_desc': 'What every shop charges, and who to ask when nobody lists it.',
  'home.tile.stock_desc': 'What the platform holds itself.',
  'home.tile.supply_orders_desc': 'What the desk has ordered from you.',
  'home.turn_what_customers_are_asking_for':
    'Turn what customers are asking for into locked deals.',
  'home.what_you_can_do': 'What you can do',
  'home.you_sit_between_the_shop_and': 'You sit between the shop and the site.',
  'login.10_digits_starting_with_9': '10 digits, starting with 9.',
  'login.forgotten_your_password_ask_the_office':
    'Forgotten your password? Ask the office to reset it for you — they will give you a new one.',
  'login.sign_in': 'Sign in',
  'login.use_the_mobile_number_your_account':
    'Use the mobile number your account was created with.',
  'money.account': 'Account',
  'money.cash_recorded': 'Cash recorded',
  'money.cheque_number_who_handed_it_over': 'Cheque number, who handed it over…',
  'money.credit': 'Credit',
  'money.customers_owe_us': 'Customers owe us',
  'money.customers_who_owe_us': 'Customers who owe us',
  'money.debit': 'Debit',
  'money.every_posting_both_sides_newest_first':
    'Every posting, both sides, newest first. Nothing here is ever edited — a correction is another entry.',
  'money.how': 'How',
  'money.in_less_out': 'In, less out.',
  'money.last_moved': 'last moved',
  'money.margin_tolowercase': 'Margin, {tolowercase}',
  'money.nobody_owes_us_anything_right_now': 'Nobody owes us anything right now.',
  'money.outstanding_right_now_balanceminor': 'Outstanding right now: {balanceminor}.',
  'money.we_are_square_with_every_shop': 'We are square with every shop.',
  'money.part_payments_are_fine_record_what':
    'Part payments are fine — record what actually changed hands.',
  'money.payment_recorded': 'Payment recorded',
  'money.record_payment': 'Record payment',
  'money.sales_minus_what_the_goods_cost': 'Sales minus what the goods cost. Not cash.',
  'money.shops_we_owe': 'Shops we owe',
  'money.text_paid_us': '{text} paid us',
  'money.the_journal': 'The journal',
  'money.we_owe_shops': 'We owe shops',
  'money.we_paid_text': 'We paid {text}',
  'money.what': 'What',
  'money.what_is_owed_in_each_direction':
    'What is owed in each direction, and the margin between the two.',
  'nav.cart': 'Basket',
  'nav.group.work': 'Workspace',
  'nav.home': 'Home',
  'nav.price_books': 'Arrangements',
  'not_found.go_home': 'Go home',
  'not_found.that_page_doesnt_exist': "That page doesn't exist",
  'not_found.the_link_may_be_out_of': 'The link may be out of date.',
  'notification_bell.notifications_unread_unread': 'Notifications, {unread} unread',
  'notifications.alerts': 'Alerts',
  'notifications.alerts_on_this_device': 'Alerts on this device',
  'notifications.allow_in_your_browser': 'Allow notifications in your browser to turn this on.',
  'notifications.browser_cannot_show_alerts': 'This browser cannot show alerts.',
  'notifications.everything_waiting_on_you_newest_first':
    'Everything waiting on you, newest first.',
  'notifications.get_an_alert_for_new_quotes':
    'Get an alert for new quotes and orders, even when the app is closed.',
  'notifications.mark_all_read': 'Mark all read',
  'notifications.new_price_requests_quotes_and_order':
    'New price requests, quotes and order updates land here.',
  'notifications.nothing_yet': 'Nothing yet',
  'notifications.older': 'Older',
  'notifications.push_not_set_up': 'Push is not set up on this server.',
  'notifications.this_device_gets_an_alert_even':
    'This device gets an alert even when the app is closed.',
  'notifications.turned_off': 'Turned off',
  'notifications.you_will_be_notified_even_when':
    'You will be notified even when the app is closed',
  'people.a_supplier_who_lists_items': 'A supplier who lists items',
  'people.accounts': 'Accounts',
  'people.add_a_person': 'Add a person',
  'people.add_person': 'Add person',
  'people.business': 'Business',
  'people.buys_from_you': 'Buys from you',
  'people.choose_a_shop': 'Choose a shop',
  'people.copied': 'Copied',
  'people.copy_password': 'Copy password',
  'people.create_account': 'Create account',
  'people.create_accounts_for_your_staff_your':
    'Create accounts for your staff, your supplier shops and your customers.',
  'people.done': 'Done',
  'people.everyone': 'Everyone',
  'people.everyone_who_can_sign_in_accounts':
    'Everyone who can sign in. Accounts are created here and the password is handed over in person.',
  'people.full_name': 'Full name',
  'people.hand_this_to': 'Hand this to',
  'people.last_signed_in_lastloginat': 'Last signed in {lastloginat}',
  'people.loading': 'Loading…',
  'people.name_can_sign_in_again': '{name} can sign in again.',
  'people.never_signed_in': 'Never signed in',
  'people.no_business_buying_on_their_own': 'No business — buying on their own',
  'people.no_people_yet': 'No people yet',
  'people.optional_leave_it_blank_for_someone':
    'Optional — leave it blank for someone buying on their own.',
  'people.optional_not_used_for_sign_in': 'Optional. Not used for sign-in.',
  'people.password_not_set': 'Password not set',
  'people.reset': 'Reset',
  'people.reset_password': 'Reset password',
  'people.role': 'Role',
  'people.search_by_name_or_mobile': 'Search by name or mobile',
  'people.staff': 'Staff',
  'people.they_are_signed_out_immediately_and':
    'They are signed out immediately and cannot sign in again until reactivated. Their orders and messages stay where they are.',
  'people.they_sign_in_with_this_it': 'They sign in with this. It must be a 10-digit mobile.',
  'people.they_will_be_able_to_sign':
    'They will be able to sign in again with their existing password.',
  'people.they_will_be_asked_to_choose':
    'They will be asked to choose their own password the first time they sign in.',
  'people.they_will_get_a_temporary_password':
    'They will get a temporary password to change when they first sign in.',
  'people.try_a_different_search': 'Try a different search.',
  'people.which_shop_this_person_belongs_to': 'Which shop this person belongs to.',
  'people.works_on_your_side_of_the': 'Works on your side of the platform',
  'people.write_this_down_or_read_it':
    'Write this down or read it out now. It cannot be shown again.',
  'people.your_team_only': 'Your team only',
  'product.also_called_matchedalias': 'also called "{matchedalias}" · ',
  'product.nothing_matched_term': 'Nothing matched "{term}".',
  'product.qty_and_up': '{qty} and up',
  'product.save': 'save',
  'product.suggest_it_as_a_new_item': 'Suggest it as a new item',
  'reports.accepted': 'Accepted',
  'reports.answers_in': 'Answers in',
  'reports.best_sellers': 'Best sellers',
  'reports.everything_below_downloads_as_csv_capped':
    'Everything below downloads as CSV, capped at the two thousand most recent rows.',
  'reports.margin': 'Margin',
  'reports.median_request_to_first_quote': 'Median, request to first quote',
  'reports.needs_shop_prices_on_file': 'Needs shop prices on file',
  'reports.n_days': '{count} days',
  'reports.n_days_one': '1 day',
  'reports.n_hr': '{count} hr',
  'reports.n_min': '{count} min',
  'reports.never_moved': 'Never moved',
  'reports.no_data': 'No data',
  'reports.nothing_on_our_own_shelf_yet': 'Nothing on our own shelf yet.',
  'reports.nothing_sold_in_this_period': 'Nothing sold in this period',
  'reports.nothing_to_score_yet_this_fills':
    'Nothing to score yet — this fills in once you have ordered from a few shops.',
  'reports.of_quotes_that_got_an_answer': 'Of quotes that got an answer',
  'reports.on_hand': 'On hand',
  'reports.once_orders_start_landing_the_best':
    'Once orders start landing, the best sellers appear here.',
  'reports.order_value': 'Order value',
  'reports.ordered': 'Ordered',
  'reports.orders_locked': 'Orders locked',
  'reports.quotes_won': 'Quotes won',
  'reports.shop_scorecard': 'Shop scorecard',
  'reports.spreadsheets': 'Spreadsheets',
  'reports.still_since': 'Still since',
  'reports.stock_on_the_shelf_oldest_first':
    'Stock on the shelf, oldest first. Capital in the wrong item is the quietest loss there is.',
  'reports.the_money_journal': 'The money journal',
  'reports.typical_first_reply': 'Typical first reply',
  'reports.unanswered_messages': 'Unanswered messages',
  'reports.value': 'Value',
  'reports.value_per_order': 'Value per order',
  'reports.waiting_on_the_desk': 'Waiting on the desk',
  'reports.what_is_not_moving': 'What is not moving',
  'reports.what_is_waiting_and_what_the':
    'What is waiting, and what the last few weeks were worth.',
  'reports.which_shops_accept_what_we_send': 'Which shops accept what we send them, and how fast.',
  'seller.items.add_what_you_stock_and_set':
    'Add what you stock and set your rates. The desk buys straight off them, so orders can arrive without anybody asking first.',
  'seller.items.add_your_first_item': 'Add your first item',
  'seller.items.anything_else_we_should_know': 'Anything else we should know',
  'seller.items.back_on_sale': 'Back on sale',
  'seller.items.blank_template': 'Blank template',
  'seller.items.download_my_price_list': 'Download my price list',
  'seller.items.download_what_you_have_edit_it':
    'Download what you have, edit it in Excel, upload it back. One row per price break.',
  'seller.items.edit_name': 'Edit {name}',
  'seller.items.from': 'from',
  'seller.items.in_stock': 'in stock',
  'seller.items.item_added': 'Item added',
  'seller.items.itemsupdated_updated_length_row_s_skipped':
    '{itemsupdated} updated, {length} row(s) skipped',
  'seller.items.leave_blank': 'Leave blank',
  'seller.items.line': 'Line',
  'seller.items.n_items_updated': '{count} items updated',
  'seller.items.n_items_updated_one': '1 item updated',
  'seller.items.n_rows_skipped': '{count} rows skipped',
  'seller.items.n_rows_skipped_one': '1 row skipped',
  'seller.items.nothing_listed_yet': 'Nothing listed yet',
  'seller.items.pause': 'Pause',
  'seller.items.pause_this_item': 'Pause this item',
  'seller.items.paused': 'Paused',
  'seller.items.pick_from_the_shared_list_so':
    'Pick from the shared list so your price can be compared. Cannot find it? Suggest it and we will confirm shortly.',
  'seller.items.price_list_as_a_spreadsheet': 'Price list as a spreadsheet',
  'seller.items.put_back_on_sale': 'Put back on sale',
  'seller.items.reading': 'Reading…',
  'seller.items.resume': 'Resume',
  'seller.items.search_your_items': 'Search your items',
  'seller.items.sent_well_confirm_this_item_shortly': "Sent — we'll confirm this item shortly",
  'seller.items.suggest_an_item': 'Suggest an item',
  'seller.items.tell_us_what_it_is_and':
    'Tell us what it is and we will add it to the shared list.',
  'seller.items.type_what_you_call_it': 'Type what you call it…',
  'seller.items.upload_a_price_list': 'Upload a price list',
  'seller.items.what_is_it_called': 'What is it called',
  'seller.items.what_you_sell_what_you_charge':
    'What you sell, what you charge, and how the price drops on a bigger order.',
  'seller.items.which_item': 'Which item',
  'seller.items.your_rate_and_how_it_drops':
    'Your rate, and how it drops as the order gets bigger.',
  'seller.price_checks.already_priced': 'Already priced',
  'seller.price_checks.answer_by_when': 'answer by {when}',
  'seller.price_checks.cannot_supply': 'Cannot supply',
  'seller.price_checks.comes_to': 'comes to',
  'seller.price_checks.deadline_passed': 'deadline passed',
  'seller.price_checks.delivered_to_city': ', delivered to {city}',
  'seller.price_checks.each': 'each',
  'seller.price_checks.for_unitcodedeliverycity': 'For {unitcode}{deliverycity}.',
  'seller.price_checks.how_much_you_can_supply': 'How much you can supply',
  'seller.price_checks.nothing_to_price': 'Nothing to price',
  'seller.price_checks.optional': 'Optional',
  'seller.price_checks.passed': 'Passed',
  'seller.price_checks.passed_on_this_one': 'Passed on this one',
  'seller.price_checks.price_sent': 'Price sent',
  'seller.price_checks.send_a_price': 'Send a price',
  'seller.price_checks.send_price': 'Send price',
  'seller.price_checks.send_your_best_rate_the_quantity':
    'Send your best rate. The quantity is what matters — quote for the whole lot.',
  'seller.price_checks.to': 'to',
  'seller.price_checks.waiting_for_you': 'Waiting for you',
  'seller.price_checks.when_we_need_a_rate_for':
    'When we need a rate for something you stock, it lands here.',
  'seller.price_checks.you_quoted': 'You quoted',
  'seller.price_checks.your_rate_each': 'Your rate each',
  'seller.supply_orders.asked_when': 'asked {when}',
  'seller.supply_orders.cant_do_it': "Can't do it",
  'seller.supply_orders.each': 'each',
  'seller.supply_orders.keep_your_prices_current_and_answer':
    'Keep your rates current — most orders come straight off them, and the rest from price checks.',
  'seller.supply_orders.nothing_to_fill_right_now': 'Nothing to fill right now',
  'seller.supply_orders.order_accepted': 'Order accepted',
  'seller.supply_orders.order_turned_down': 'Order turned down',
  'seller.supply_orders.the_desk_is_told_straight_away':
    'The desk is told straight away and will source it elsewhere. You cannot take it back afterwards.',
  'seller.supply_orders.turn_down_code': 'Turn down {code}?',
  'seller.supply_orders.turn_this_down': 'Turn this down?',
  'seller.supply_orders.wanted_by_when': 'wanted by {when}',
  'seller.supply_orders.what_the_desk_has_ordered_from':
    'What the desk has ordered from you. Accept it and get it ready, or pass.',
  'settings.advanced.bulk_publish': 'Publish or hide a whole page of items at once',
  'settings.advanced.export': 'Spreadsheet downloads',
  'settings.advanced.ledger': 'The money journal, both sides of every posting',
  'settings.advanced.price_books':
    'Customer arrangements — standing rates for particular customers',
  'settings.advanced.price_import': 'Your price list as a spreadsheet, in and out',
  'settings.advanced.reports': 'Stock aging and the shop scorecard',
  'settings.advanced_mode': 'Advanced mode',
  'settings.change_it_whenever_you_like_you':
    'Change it whenever you like. You will need the password you use now.',
  'settings.change_password': 'Change password',
  'settings.change_your_password': 'Change your password',
  'settings.dark_is_easier_on_the_eyes':
    'Dark is easier on the eyes indoors; light is easier to read in daylight on site.',
  'settings.it_changes_what_you_are_shown':
    "It changes what you are shown, never what you are allowed to do. Nobody else's screen moves.",
  'settings.language': 'Language',
  'settings.mobile': 'Mobile',
  'settings.mobile_number_and_password': 'Mobile number and password',
  'settings.off_by_default_everything_the_days':
    "Off by default. Everything the day's work needs is on the screen without it — this adds the tools for the occasional deep question.",
  'settings.on': 'On',
  'settings.signs_in_with': 'Signs in with',
  'settings.the_app_in_english_or_nepali':
    'The app in English or Nepali. It follows your account, so any device you sign in on matches.',
  'settings.you_stay_signed_in_here_anywhere':
    'You stay signed in here. Anywhere else you are signed in gets signed out.',
  'settings.your_setup': 'Your setup',
  'setup.at_least_minpasswordlength_characters': 'At least {minpasswordlength} characters.',
  'setup.create_owner_account': 'Create owner account',
  'setup.set_up_your_platform': 'Set up your platform',
  'setup.shown_to_your_customers_and_suppliers': 'Shown to your customers and suppliers.',
  'setup.this_creates_the_owner_account_it':
    'This creates the owner account. It only happens once.',
  'setup.you_will_sign_in_with_this': 'You will sign in with this.',
  'setup.your_mobile_number': 'Your mobile number',
  'setup.your_name': 'Your name',
  'shop.browse.a_to_z': 'A to Z',
  'shop.browse.ask_us': 'Ask us',
  'shop.browse.best_match': 'Best match',
  'shop.browse.cement_rebar_wire_paint': 'Cement, rebar, wire, paint…',
  'shop.browse.cheapest_first': 'Cheapest first',
  'shop.browse.dearest_first': 'Dearest first',
  'shop.browse.down_to': 'down to',
  'shop.browse.everything_we_supply': 'Everything we supply',
  'shop.browse.in_bulk': 'in bulk',
  'shop.browse.items_appear_here_as_soon_as': 'Items appear here as soon as we price them.',
  'shop.browse.nothing_here_yet': 'Nothing here yet',
  'shop.browse.nothing_matched_debounced': 'Nothing matched "{debounced}"',
  'shop.browse.prices_drop_as_the_quantity_goes':
    'Prices drop as the quantity goes up. Add what you need and ask us for a better rate.',
  'shop.browse.sort': 'Sort',
  'shop.browse.try_a_shorter_search_or_message':
    'Try a shorter search, or message us — we can usually source it.',
  'shop.cart.anything_else': 'Anything else',
  'shop.cart.ask_for_a_better_price': 'Ask for a better price',
  'shop.cart.buy_at_listed_prices': 'Buy at listed prices',
  'shop.cart.delete_name': 'Delete {name}',
  'shop.cart.delivery_is_quoted_separately_we_confirm':
    'Delivery is quoted separately. We confirm the final figure before anything is agreed.',
  'shop.cart.empty_it': 'Empty it',
  'shop.cart.find_what_you_need_in_the':
    'Find what you need in the shop and add it here. Quantities can be changed any time.',
  'shop.cart.get_my_quote': 'Get my quote',
  'shop.cart.give_it_a_name_you_will':
    'Give it a name you will recognise — it keeps the items and the quantities, not the prices.',
  'shop.cart.monthly_cement_order': 'Monthly cement order',
  'shop.cart.more_saves': 'more saves',
  'shop.cart.no_longer_priced_remove_it_or': 'No longer priced — remove it or ask us',
  'shop.cart.nothing_in_your_basket': 'Nothing in your basket',
  'shop.cart.per': 'per',
  'shop.cart.put_in_basket': 'Put in basket',
  'shop.cart.save_this_address_as': 'Save this address as',
  'shop.cart.save_this_basket': 'Save this basket',
  'shop.cart.save_this_basket_and_it_is':
    'Save this basket and it is two taps to rebuild next month.',
  'shop.cart.save_this_basket_as_a_list': 'Save this basket as a list',
  'shop.cart.send_the_request': 'Send the request',
  'shop.cart.sent_well_come_back_to_you': "Sent — we'll come back to you shortly",
  'shop.cart.set_the_quantities_then_either_buy':
    'Set the quantities, then either buy at the listed price or ask us for a better one.',
  'shop.cart.site_address': 'Site address',
  'shop.cart.site_yard_head_office_leave_blank':
    'Site, Yard, Head office… (leave blank not to save it)',
  'shop.cart.somewhere_else': 'Somewhere else…',
  'shop.cart.tell_usthedesk_what_you_need_and':
    'Tell {usthedesk} what you need and we will come back with a rate.',
  'shop.cart.timing_access_anything_we_should_know': 'Timing, access, anything we should know.',
  'shop.cart.total_at_listed_prices': 'Total at listed prices',
  'shop.cart.we_will_put_the_quote_together':
    'We will put the quote together straight away so you can accept it.',
  'shop.cart.what_rate_are_you_hoping_for': 'What rate are you hoping for? When do you need it?',
  'shop.cart.where_should_it_go': 'Where should it go?',
  'shop.cart.your_lists': 'Your lists',
  'shop.cart.your_quote_is_ready_to_accept': 'Your quote is ready to accept',
  'shop.product.add_more_and_save': 'Add {qty} more and save {percent}% on every one.',
  'shop.product.add_to_basket': 'Add to basket',
  'shop.product.added_to_your_basket': 'Added to your basket',
  'shop.product.back_to_the_shop': 'Back to the shop',
  'shop.product.each': 'each',
  'shop.product.how_many_do_you_need': 'How many do you need?',
  'shop.product.more_in': 'More in',
  'shop.product.n_days': '{count} days',
  'shop.product.n_days_one': '1 day',
  'shop.product.one_unit_contains_content': 'One {unit} contains {content}.',
  'shop.product.vat_on_top': 'VAT on top',
  'shop.product.your_price': 'Your price',
  'stock.correct_a_count': 'Correct a count',
  'stock.count_corrected': 'Count corrected',
  'stock.counted_quantity': 'Counted quantity',
  'stock.damaged_in_transit': 'Damaged in transit',
  'stock.delivery_note_number_optional': 'Delivery note number, optional',
  'stock.delivery_recorded': 'Delivery recorded',
  'stock.enter_the_count_you_just_made':
    'Enter the count you just made. We work out the difference.',
  'stock.free': 'free',
  'stock.how_much_arrived': 'How much arrived',
  'stock.inventory': 'Inventory',
  'stock.left': 'left',
  'stock.movements_appear_here_as_they_happen': 'Movements appear here as they happen.',
  'stock.no_stock_held': 'No stock held',
  'stock.nothing_is_running_low': 'Nothing is running low',
  'stock.nothing_recorded_yet': 'Nothing recorded yet',
  'stock.older_movements': 'Older movements',
  'stock.record_a_delivery_and_it_will':
    'Record a delivery and it will appear here with its own ledger.',
  'stock.save_the_count': 'Save the count',
  'stock.search_stock': 'Search stock',
  'stock.set_a_reorder_point_on_an':
    'Set a reorder point on an item to be warned before it runs out.',
  'stock.set_aside': 'set aside',
  'stock.show_every_movement': 'Show every movement',
  'stock.stock_ledger': 'Stock ledger',
  'stock.take_a_delivery': 'Take a delivery',
  'stock.take_delivery': 'Take delivery',
  'stock.what_arrived_and_how_much_of': 'What arrived, and how much of it.',
  'stock.what_happened': 'What happened',
  'stock.what_the_platform_holds_itself_everything':
    'What the platform holds itself. Everything else is ordered in from a shop.',
  'theme.dark': 'Dark',
  'theme.light': 'Light',
  'theme.match_device': 'Match device',
  'tier_editor.add_a_price_break': 'Add a price break',
  'tier_editor.check_a_quantity': 'Check a quantity',
  'tier_editor.each': 'each =',
  'tier_editor.example_quantity': 'Example quantity',
  'tier_editor.fix_the_rows_above_to_see': 'Fix the rows above to see an example.',
  'tier_editor.from': 'From',
  'tier_editor.from_to': '{from} to {to}',
  'tier_editor.over_cost': 'over cost',
  'tier_editor.price_curve': 'Price curve',
  'tier_editor.qty_and_above': '{qty} and above',
  'tier_editor.qty_at': '{qty} at',
  'tier_editor.remove_this_price_row': 'Remove this price row',
  'tour.guide.description':
    'Short walkthroughs of the screens you use. Take one whenever you like, as often as you like.',
  'tour.guide.open': 'Open the guide',
  'tour.show_me_around': 'Show me around',
  'ui.money.less_name': 'Less {name}',
  'ui.money.margin': 'margin',
  'ui.money.more_name': 'More {name}',
  'ui.primitives.that_didnt_load': "That didn't load",
  'ui.primitives.try_again': 'Try again',
  'ui.view.how_to_show_the_list': 'How to show the list',
  'ui.view.show_as_a_grid': 'Show as a grid',
  'ui.view.show_as_a_list': 'Show as a list',
} as const

export type MessageKey = keyof typeof en
export type Dictionary = Record<MessageKey, string>
